import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import useLenis from "../hooks/useLenis";
import useSeo from "../hooks/useSeo";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { CapabilityDeck } from "../components/CapabilityDeck";
import { Reveal } from "../lib/reveal";
import { getRegion } from "../data/regions";
import { caseStudies } from "../data/caseStudies";
import { citiesByState } from "../data/cities";
import { MapPin, ArrowUpRight, ArrowLeft } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

export default function Region() {
  useLenis();
  const { slug } = useParams();
  const { lang, t } = useLang();
  const region = getRegion(slug);

  useSeo(
    region
      ? {
          title: `OED in ${region.state} — Landfill Odour Control & Bio-mining | Fresh Earth`,
          description: `${region.intro} Serving ${region.cities.join(", ")}.`,
          path: `/regions/${slug}`,
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: "Landfill odour control & biological waste remediation",
            areaServed: { "@type": "State", name: region.state },
            provider: { "@type": "Organization", name: "Fresh Earth" },
            description: region.intro,
          },
        }
      : { title: "Region not found · Fresh Earth", path: `/regions/${slug}` }
  );

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!region) {
    return (
      <div className="bg-bone text-ink min-h-screen flex flex-col items-center justify-center">
        <p className="font-serif text-3xl">Region not found.</p>
        <Link to="/" className="mt-4 underline text-moss">Back home</Link>
      </div>
    );
  }

  const cases = caseStudies.filter((c) => region.deployments.includes(c.slug));
  const cityPages = citiesByState(region.slug);
  const H = lang === "hi" ? (region.hi || {}) : {};

  return (
    <div className="bg-bone text-ink min-h-screen" data-testid="region-page">
      <Navbar />
      <main className="pt-32 md:pt-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Link to="/" className="inline-flex items-center gap-2 text-sm tracking-[0.08em] uppercase text-slate hover:text-ink transition-colors duration-300">
            <ArrowLeft size={16} /> {t("Home", "होम")}
          </Link>

          <Reveal className="mt-8 flex items-center gap-3">
            <MapPin size={18} className="text-terracotta" />
            <span className="text-xs tracking-[0.22em] uppercase font-semibold text-moss">Fresh Earth · {region.state}</span>
          </Reveal>
          <Reveal>
            <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95] mt-5 max-w-4xl">{t(region.headline, H.headline)}</h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg text-slate max-w-2xl">{t(region.intro, H.intro)}</p>
          </Reveal>

          {/* Stats */}
          <Reveal delay={0.15}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-14">
              {region.stats.map((s, i) => (
                <div key={s.l} className="bg-sand rounded-2xl border border-stone/20 p-8">
                  <div className="font-serif text-4xl md:text-5xl text-moss leading-none">{s.v}</div>
                  <div className="text-xs tracking-[0.1em] uppercase text-ink mt-4">{t(s.l, H.stats?.[i])}</div>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Focus + cities */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-16">
            <Reveal>
              <h2 className="font-serif text-2xl md:text-3xl mb-3">{t("Who we serve here", "हम यहाँ किसकी सेवा करते हैं")}</h2>
              <p className="text-base leading-relaxed text-slate">{t(region.focus, H.focus)}</p>
            </Reveal>
            <Reveal>
              <h2 className="font-serif text-2xl md:text-3xl mb-3">{t("Cities in focus", "केंद्रित शहर")}</h2>
              <div className="flex flex-wrap gap-2 mt-2">
                {region.cities.map((c) => (
                  <span key={c} className="text-sm tracking-[0.06em] text-moss border border-moss/30 rounded-full px-4 py-2">{c}</span>
                ))}
              </div>
              {cityPages.length > 0 && (
                <div className="mt-6">
                  <div className="text-xs tracking-[0.14em] uppercase text-stone mb-3">{t("Explore city pages", "शहर पृष्ठ देखें")}</div>
                  <div className="flex flex-wrap gap-2">
                    {cityPages.map((c) => (
                      <Link
                        key={c.slug}
                        to={`/cities/${c.slug}`}
                        data-testid={`region-city-link-${c.slug}`}
                        className="inline-flex items-center gap-1.5 text-sm text-forest bg-sand border border-stone/20 rounded-full px-4 py-2 hover:border-moss transition-colors duration-300"
                      >
                        {c.city} <ArrowUpRight size={14} />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </Reveal>
          </div>

          {/* Deployments in this state */}
          {cases.length > 0 && (
            <div className="mt-20">
              <Reveal>
                <h2 className="font-serif text-3xl md:text-4xl mb-8">{t(`Deployments in ${region.state}`, `${region.state} में तैनातियाँ`)}</h2>
              </Reveal>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {cases.map((c, i) => (
                  <Reveal key={c.slug} delay={i * 0.08}>
                    <Link to={`/case-studies/${c.slug}`} data-testid={`region-case-${c.slug}`} className="group block bg-sand rounded-3xl border border-stone/20 overflow-hidden hover:border-moss transition-colors duration-400">
                      <div className="relative aspect-[16/9] overflow-hidden">
                        <img src={c.afterImg} alt={c.city} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                        <span className="absolute top-4 left-4 text-[0.68rem] tracking-[0.16em] uppercase bg-bone/90 text-forest px-3 py-1.5 rounded-full">{c.tag}</span>
                      </div>
                      <div className="p-7">
                        <h3 className="font-serif text-2xl text-ink leading-tight">{t(c.headline, c.hi?.headline)}</h3>
                        <span className="inline-flex items-center gap-1 text-sm text-moss mt-3">{t("Read case study", "केस स्टडी पढ़ें")} <ArrowUpRight size={15} /></span>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>
          )}

          {/* Gated capability deck */}
          <div className="mt-20">
            <CapabilityDeck context={region.state} />
          </div>

          {/* CTA */}
          <Reveal>
            <div className="my-20 bg-forest text-bone rounded-3xl p-10 md:p-14 grain relative overflow-hidden">
              <p className="font-serif text-3xl md:text-4xl leading-snug relative max-w-2xl">{t(`Have a site in ${region.state}? Start with a low-risk pilot.`, `${region.state} में कोई साइट है? एक कम-जोखिम वाले पायलट से शुरुआत करें।`)}</p>
              <Link to="/#contact" className="inline-flex mt-8 items-center rounded-full bg-bone text-forest text-sm tracking-[0.1em] uppercase px-8 py-4 hover:bg-terracotta hover:text-bone transition-colors duration-300 relative">
                {t("Request a Pilot", "पायलट का अनुरोध करें")}
              </Link>
            </div>
          </Reveal>
        </div>
      </main>
      <Footer />
    </div>
  );
}
